import { StyleSheet } from 'aphrodite';
import TileBackgroundImageUrl from '../../../assets/surveyTileBackground.png';
import { THEME } from '../../../components-v2/Theme/theme';
import {
	borderColor,
	brandPrimary,
	brandPrimaryHover,
	charmGray,
	destructive,
	gray4,
	lightGray,
	titles,
	white,
} from '../../../styles/colors';
import { CssSelectors } from '../../../styles/styles';

const tabViewHeaderHeight = 185;
const {
	spacing,
	colors,
	typography: { fontSizes, fontWeights },
	common,
} = THEME;

export const styleSheet = StyleSheet.create({
	container: {
		position: 'relative',
		padding: `${spacing.md}px 0 ${spacing.xl}px`,
		background: white,
		boxSizing: 'border-box',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		position: 'relative',
		zIndex: 1,
		background: white,
		marginLeft: `-${spacing.md}px`,
		marginRight: `-${spacing.md}px`,
		marginBottom: 0,
		padding: `0 ${spacing.md}px 0`,
		...CssSelectors.allDescendants(
			{
				margin: `0 0 ${spacing.sm}px`,
				color: charmGray,
				fontSize: fontSizes.sm,
			},
			'label'
		),
	},
	editor: {
		background: lightGray,
		border: common.border,
		height: 170,
		width: '100%',
		maxWidth: 725,
	},
	headerNameWrap: {
		height: 59,
	},
	headerNameInputWrap: {
		minHeight: 50,
		marginBottom: spacing.sm,
	},
	headerTitle: {
		fontSize: fontSizes.md,
		lineHeight: `${fontSizes.md}px`, // must be in px
		color: titles,
		margin: spacing.none,
		paddingBottom: spacing.none,
		maxWidth: 725,
	},
	editPen: {
		position: 'absolute',
		top: 0,
		right: `-${spacing.lg}px`,
	},
	formMenu: {
		display: 'flex',
		gap: spacing.md,
		alignItems: 'center',
	},
	bottomSection: {
		background: white,
		position: 'relative',
		marginBottom: spacing.xl,
		boxShadow: `0 0 6px 0 ${borderColor}`,
		borderRadius: 8,
		borderLeft: `4px solid transparent`,
	},
	bottomSectionNoValue: {
		borderLeft: `4px solid ${destructive}`,
	},
	sectionFullWidth: {},
	section: {
		padding: `${spacing.md}px ${spacing.md}px 0`,
		border: common.border,
	},
	sectionNoBorderTop: {
		borderTop: 0,
	},
	sectionTitle: {
		padding: `${spacing.md}px ${spacing.md}px ${spacing.sm}px`,
	},
	footer: {
		width: '100%',
	},
	addQuestionButton: {
		width: '100%',
		border: `2px dashed ${brandPrimary}`,
		color: brandPrimary,
		borderRadius: common.borderRadius,
		fontSize: fontSizes.sm,
		padding: `${spacing.sm}px 0`,
		textAlign: 'center',
		backgroundColor: THEME.colors.pill.teal.background,
	},
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		justifyContent: 'flex-start',
		width: '100%',
		alignItems: 'center',
		padding: `${spacing.xxl}px 0`,
		backgroundColor: THEME.colors.background.light,
		position: 'relative',
	},
	reorderSlot: {
		position: 'sticky',
		top: 0,
		zIndex: 1,
		width: 'fit-content',
		paddingTop: spacing.xxl,
		paddingLeft: spacing.lg,
	},
	additionalQuestionsContainer: {
		width: '100%',
		zIndex: 1,
		position: 'relative',
		display: 'flex',
		flexDirection: 'row',
		// stretch so the gutter spacers take the column's full height — gives the sticky
		// reorder slot a range to pin within (flex-start would collapse it to 0 height).
		alignItems: 'stretch',
	},
	// Editor column (V1 + V2) constrained to 766; centred by the equal side spacers.
	editorContentColumn: {
		flexGrow: 0,
		flexShrink: 1,
		flexBasis: 766,
		maxWidth: 766,
		minWidth: 0,
	},
	questionsLoadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 400,
		width: '100%',
	},
	// Equal-flex gutters either side centre the column; the right one hosts the sticky
	// reorder slot (V2 only). No magic offsets — scales with viewport width.
	editorSideSpacer: {
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: 0,
		minWidth: 0,
	},
	bodyBackground: {
		backgroundImage: `url(${TileBackgroundImageUrl})`,
		backgroundRepeat: 'repeat',
		backgroundSize: `${spacing.xxxl}px`,
		bottom: 0,
		content: '',
		display: 'block',
		height: '100%',
		left: 0,
		opacity: 0.2,
		position: 'absolute',
		right: 0,
		top: 0,
		width: '100%',
		zIndex: 1,
	},
	tabViewContainer: {
		position: 'relative',
		zIndex: 1,
		height: `calc(100vh - ${tabViewHeaderHeight}px)`,
	},
	tabList: {
		position: 'relative',
		zIndex: 1,
		background: white,
		display: 'flex',
		flexDirection: 'row',
		gap: spacing.sm,
		minHeight: spacing.xl,
		...CssSelectors.allDescendants(
			{
				width: 'auto',
				flex: 0,
				textTransform: 'capitalize',
				fontSize: fontSizes.sm,
				padding: `${spacing.sm}px ${spacing.md}px`,
				borderBottom: `2px solid transparent`,
				color: gray4,
				display: 'flex',
				gap: spacing.sm,
				alignItems: 'center',
				height: spacing.xl,
			},
			'button'
		),
		...CssSelectors.allDescendants(
			{
				color: brandPrimary,
				borderBottom: `2px solid ${brandPrimary}`,
			},
			'.tabbar-item-selected'
		),
	},
	tabTextError: {
		color: `${destructive} !important`,
		...CssSelectors.allDescendants(
			{
				fill: `${destructive} !important`,
			},
			'svg path'
		),
	},
	formControl: {
		marginBottom: spacing.md,
		maxWidth: 725,
		...CssSelectors.allChildren(
			{
				color: charmGray,
				fontSize: fontSizes.md,
				marginBottom: spacing.sm,
				display: 'inline-flex',
			},
			'label'
		),
	},
	tabsControllerWrap: {
		paddingBottom: spacing.xxl,
		...CssSelectors.allDescendants(
			{
				margin: `${spacing.md}px 0`,
			},
			'h3'
		),
	},
	radioButtons: {
		display: 'inline-flex',
		gap: spacing.sm,
		flexDirection: 'column',
		...CssSelectors.allDescendants(
			{
				color: charmGray,
				fontSize: fontSizes.sm,
				marginBottom: spacing.sm,
				display: 'inline-flex',
			},
			'label'
		),
	},
	calendarDateRangeButton: {
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
		color: titles,
		fontSize: fontSizes.sm,
		maxWidth: 240,
		borderRadius: common.borderRadius,
		padding: `${spacing.sm}px ${spacing.md}px`,
		background: borderColor,
		'&:hover': {
			color: titles,
		},
	},
	previewLink: {
		textDecoration: 'none',
		color: brandPrimary,
		fontSize: fontSizes.sm,
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
		'&:hover': {
			color: brandPrimaryHover,
			...CssSelectors.allDescendants(
				{
					color: brandPrimaryHover,
				},
				'svg path'
			),
		},
	},
	removeButtonIcon: {
		width: spacing.md,
		height: spacing.md,
		color: colors.text.light,
	},
	sectionDescription: {
		color: colors.text.light,
		margin: `0 0 ${spacing.md}px 0`,
		whiteSpace: 'nowrap',
	},
	notificationTitle: {
		marginBottom: spacing.sm,
		width: '100%',
	},
	notificationEmail: {
		width: '100%',
		minWidth: 417,
		...CssSelectors.allChildren(
			{
				':hover': {
					cursor: 'not-allowed',
				},
			},
			'input'
		),
	},
	notificationRecipientInput: {
		width: '100%',
		...CssSelectors.allChildren(
			{
				width: '100%',
			},
			'input'
		),
	},
	tabContent: {
		background: THEME.colors.background.light,
		padding: `${spacing.md}px ${spacing.lg}px 0`,
		margin: `0 -${spacing.lg}px`,
		...CssSelectors.allChildren(
			{
				padding: `0 ${spacing.lg}px`,
				margin: `0 -${spacing.lg}px`,
				background: THEME.colors.background.light,
			},
			'div'
		),
	},
	newTabContent: {
		flexGrow: 1,
	},
	tabContentHidden: {
		display: 'none',
	},
	page: {
		background: THEME.colors.background.light,
	},
	notificationSettingsContainer: {
		maxWidth: 725,
	},
	notificationSection: {
		marginBottom: spacing.xxxl,
	},
	additionalRecipientsLabel: {
		display: 'block',
		marginBottom: spacing.sm,
		color: colors.text.light,
	},
	additionalRecipient: {
		marginBottom: spacing.md,
		minWidth: 417,
		...CssSelectors.allDescendants(
			{
				...CssSelectors.allChildren(
					{
						width: '100%',
					},
					'div:first-child'
				),
			},
			'div'
		),
	},
	removeButton: {
		color: colors.text.main,
		...CssSelectors.allChildren(
			{
				...CssSelectors.allDescendants(
					{
						color: 'transparent',
						fontSize: fontSizes.none,
					},
					'span'
				),
			},
			'span'
		),
	},
	fieldset: {
		border: spacing.none,
		margin: spacing.none,
		padding: spacing.none,
		boxSizing: 'border-box',
		maxWidth: 725,
	},
	fieldsetDisabled: {
		opacity: 0.5,
	},
	errorMessage: {
		margin: 0,
		color: colors.danger.main,
		fontSize: fontSizes.sm,
	},
	notificationToggle: {
		display: 'flex',
	},
	removeQuestionButton: {
		transform: 'translateY(-60px)',
	},
	modalTitle: {
		fontSize: fontSizes.lg,
		fontWeight: 600,
		color: titles,
		margin: `0 0 ${spacing.sm}px 0`,
	},
	modalDescription: {
		color: colors.text.light,
		fontSize: fontSizes.sm,
		margin: `0 0 ${spacing.md}px 0`,
		lineHeight: 1.5,
	},
	modalWarning: {
		color: colors.danger.main,
		fontSize: fontSizes.sm,
		margin: `${spacing.xs}px 0 ${spacing.sm}px 0`,
		lineHeight: 1.5,
	},
	modalInput: {
		width: '100%',
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	modalCancelButton: {},
	modalSaveButton: {},
	modalDeleteButton: {
		backgroundColor: colors.background.light,
		color: colors.danger.main,
		border: `1px solid ${colors.danger.main}`,
		padding: `${spacing.xs}px ${spacing.md}px`,
		borderRadius: common.borderRadius,
		'&:hover': {
			backgroundColor: colors.background.main,
			color: colors.danger.main,
			border: `1px solid ${colors.danger.main}`,
		},
	},
	afterInputIcon: {
		top: 12,
		right: 8,
		...CssSelectors.allDescendants(
			{
				fill: THEME.colors.pill.blue.icon,
				cursor: 'pointer',
			},
			'svg path'
		),
	},
	infoIcon: {
		width: spacing.md,
		height: spacing.md,
		cursor: 'pointer',
		color: colors.icon.primary,
	},
	infoPopoverContent: {
		maxWidth: 350,
		fontSize: fontSizes.sm,
	},
	/** FormFieldWrapper's default text column reserves `min-width: 85%`, which leaves too little
	 *  room for the contact-collection dropdown once its label grows past "Required" — the flex row
	 *  then wraps the dropdown onto its own line. Releasing the floor lets the text column yield
	 *  space to the control instead. */
	contactCollectionTextWrapper: {
		minWidth: 0,
	},
	/** Sized for the longest option ("Do Not Collect") so the dropdown keeps one width across
	 *  selections instead of resizing the row on every change. */
	contactCollectionSelectTrigger: {
		flexShrink: 0,
		width: 170,
	},
	/** Row wrapper for a partial-submission lock note: warning icon + text, shown beneath a
	 *  field's description in the left text column. */
	lockNoteRow: {
		display: 'flex',
		alignItems: 'center',
		gap: `${spacing.xs}px`,
		marginTop: `${spacing.xs}px`,
	},
	lockNoteIcon: {
		width: 14,
		height: 14,
		flexShrink: 0,
	},
	/** Helper note shown beneath the partial-submission controls — used both for the
	 *  "required for partial submissions" email hint and the "can't be disabled once
	 *  enabled" lock note (identical styling). Warning color so the constraint stands out. */
	partialSubmissionNote: {
		fontSize: fontSizes.xs,
		color: colors.warning.main,
		fontWeight: fontWeights.standard,
		margin: 0,
	},
});
